import { type ReactNode, useState, useRef, useEffect } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { useLogout, useProfile, useTrialDaysLeft } from '../hooks/useAuth.js';
import { useTheme } from '../contexts/ThemeContext.js';
import { Icon, type IconName } from './ui/Icon.js';
import { Logo } from './ui/Logo.js';

const DESKTOP_NAV: { to: string; label: string }[] = [
  { to: '/dashboard',  label: 'Home' },
  { to: '/meal-plans', label: 'Meal Plans' },
  { to: '/favorites',  label: 'Favorites' },
  { to: '/deny-list',  label: 'Avoid List' },
  { to: '/achievements', label: 'Achievements' },
];

const MOBILE_NAV: { to: string; label: string; icon: IconName }[] = [
  { to: '/dashboard',  label: 'Home',    icon: 'home' },
  { to: '/meal-plans', label: 'Plan',    icon: 'plate' },
  { to: '/favorites',  label: 'Saved',   icon: 'heart' },
  { to: '/profile',    label: 'Profile', icon: 'user' },
];

function initials(name?: string | null): string {
  if (!name) return '··';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function TierBadge({ role, tier, daysLeft }: { role: string; tier: string; daysLeft: number | null }) {
  const baseCls = 'text-[10px] font-bold tracking-wider uppercase rounded-full px-2 py-0.5';
  if (role === 'admin') {
    return (
      <span className={baseCls}
        style={{ background: 'oklch(0.45 0.15 320 / 0.18)', color: 'oklch(0.7 0.16 320)' }}>
        Admin
      </span>
    );
  }
  if (tier === 'trial') {
    return (
      <span className={baseCls}
        style={{
          background: 'color-mix(in oklab, var(--warning) 18%, transparent)',
          color: 'var(--warning)',
        }}>
        Trial{daysLeft !== null ? ` · ${daysLeft}d` : ''}
      </span>
    );
  }
  return null;
}

function DropdownItem({ to, onClick, children }: { to: string; onClick: () => void; children: ReactNode }) {
  return (
    <NavLink to={to} onClick={onClick}
      className="block px-4 py-2 text-sm transition-colors hover:opacity-80"
      style={{ color: 'var(--ink-2)' }}>
      {children}
    </NavLink>
  );
}

export function Layout({ children }: { children: ReactNode }) {
  const logout = useLogout();
  const { data: profile } = useProfile();
  const daysLeft = useTrialDaysLeft(profile);
  const { darkMode, toggleDark } = useTheme();

  const [profileOpen, setProfileOpen] = useState(false);
  const profileDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleOutside(e: MouseEvent) {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(e.target as Node)) {
        setProfileOpen(false);
      }
    }
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'var(--bg-deep)' }}>
      {/* ── Trial-expiry warning ─────────────────────────────────────────── */}
      {profile?.tier === 'trial' && daysLeft !== null && daysLeft <= 7 && (
        <div className="px-4 py-2 text-center text-sm"
          style={{
            background: 'color-mix(in oklab, var(--warning) 14%, var(--surface))',
            borderBottom: '1px solid color-mix(in oklab, var(--warning) 35%, var(--line-soft))',
            color: 'var(--ink)',
          }}>
          {daysLeft === 0
            ? 'Your free trial has ended.'
            : `Your free trial ends in ${daysLeft} day${daysLeft === 1 ? '' : 's'}.`}
          {' '}
          <Link to="/profile" className="font-semibold underline" style={{ color: 'var(--warning)' }}>
            Continue your subscription →
          </Link>
        </div>
      )}

      {/* ── Top nav (sticky, glass) ──────────────────────────────────────── */}
      <header className="sticky top-0 z-40"
        style={{
          background: 'color-mix(in oklab, var(--bg) 92%, transparent)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderBottom: '1px solid var(--line-soft)',
        }}>
        <div className="flex items-center gap-2 sm:gap-4 md:gap-6 px-3 sm:px-4 md:px-8 py-2.5 sm:py-3">
          {/* Logo + (optional) tier badge */}
          <Link to="/dashboard" className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <Logo size={16} />
            <div className="hidden sm:block">
              {profile && <TierBadge role={profile.role} tier={profile.tier} daysLeft={daysLeft} />}
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-6 flex-1 ml-2" aria-label="Main navigation">
            {DESKTOP_NAV.map(({ to, label }) => (
              <NavLink key={to} to={to} end={to === '/dashboard'}
                className="relative pb-1 text-sm font-medium transition-colors"
                style={({ isActive }) => ({
                  color: isActive ? 'var(--ink)' : 'var(--ink-3)',
                  borderBottom: `2px solid ${isActive ? 'var(--accent)' : 'transparent'}`,
                })}>
                {label}
              </NavLink>
            ))}
          </nav>

          {/* Right cluster: dark toggle, settings, profile avatar+menu */}
          <div className="hidden md:flex items-center gap-1.5 ml-auto">
            <button onClick={toggleDark}
              className="w-9 h-9 flex items-center justify-center rounded-full transition-colors"
              style={{ color: 'var(--ink-3)' }}
              title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              aria-label={darkMode ? 'Light mode' : 'Dark mode'}>
              <Icon name={darkMode ? 'sun' : 'moon'} size={16} />
            </button>
            <NavLink to="/preferences" title="Preferences" aria-label="Preferences"
              className="w-9 h-9 flex items-center justify-center rounded-full transition-colors"
              style={({ isActive }) => ({
                color: isActive ? 'var(--ink)' : 'var(--ink-3)',
                background: isActive ? 'var(--surface-2)' : 'transparent',
              })}>
              <Icon name="settings" size={16} />
            </NavLink>

            <div ref={profileDropdownRef} className="relative ml-1">
              <button onClick={() => setProfileOpen(o => !o)}
                className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold cursor-pointer"
                style={{ background: 'var(--accent)', color: 'var(--accent-ink)' }}
                aria-label="Profile menu" aria-expanded={profileOpen}>
                {initials(profile?.name)}
              </button>
              {profileOpen && (
                <div className="absolute right-0 mt-2 w-48 rounded-xl py-1 z-50"
                  style={{
                    background: 'var(--surface)',
                    border: '1px solid var(--line-soft)',
                    boxShadow: 'var(--shadow-pop)',
                  }}>
                  <DropdownItem to="/profile" onClick={() => setProfileOpen(false)}>Profile</DropdownItem>
                  <DropdownItem to="/preferences" onClick={() => setProfileOpen(false)}>Preferences</DropdownItem>
                  {profile?.role === 'admin' && (
                    <DropdownItem to="/admin" onClick={() => setProfileOpen(false)}>Admin</DropdownItem>
                  )}
                  <div className="my-1 mx-3" style={{ height: 1, background: 'var(--line-soft)' }} />
                  <button onClick={() => logout.mutate()}
                    className="block w-full text-left px-4 py-2 text-sm transition-colors hover:opacity-80"
                    style={{ color: 'var(--ink-3)', fontFamily: 'inherit' }}>
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Mobile dark toggle (rest of nav lives in bottom bar) */}
          <button onClick={toggleDark}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-full ml-auto"
            style={{ color: 'var(--ink-3)' }}
            aria-label={darkMode ? 'Light mode' : 'Dark mode'}
            title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}>
            <Icon name={darkMode ? 'sun' : 'moon'} size={16} />
          </button>
        </div>
      </header>

      {/* ── Main content (pages bring their own padding/max-width) ───────── */}
      <main className="flex-1 pb-20 md:pb-0">{children}</main>

      {/* ── Mobile bottom nav (5 items) ──────────────────────────────────── */}
      <nav className="fixed bottom-0 left-0 right-0 md:hidden flex items-stretch z-40"
        style={{
          background: 'color-mix(in oklab, var(--bg) 94%, transparent)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          borderTop: '1px solid var(--line-soft)',
          paddingTop: 8,
          paddingBottom: 'calc(env(safe-area-inset-bottom) + 14px)',
        }}
        aria-label="Mobile navigation">
        {MOBILE_NAV.map(({ to, label, icon }) => (
          <NavLink key={to} to={to} end={to === '/dashboard'}
            className="flex-1 flex flex-col items-center justify-center gap-0.5 py-2 min-h-[44px]"
            aria-label={label}
            style={({ isActive }) => ({
              color: isActive ? 'var(--accent)' : 'var(--ink-3)',
              fontFamily: 'inherit',
            })}>
            <Icon name={icon} size={20} />
            <span className="text-[10px] font-semibold tracking-wide">{label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
